import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, Mail, Phone } from 'lucide-react';

const DPAPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Data Processing Agreement (DPA)
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Our commitment to protecting your customers' data in compliance with GDPR and UK data protection laws.
          </p>
          <p className="text-lg text-gray-400 mb-6">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <Badge className="bg-[#FF5A3D] text-white px-4 py-2">
            GDPR Compliant
          </Badge>
        </div>
      </section>

      {/* DPA Content */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
              <h2 className="text-xl font-semibold text-blue-900 mb-3">What is a DPA?</h2>
              <p className="text-blue-800 text-sm">
                A Data Processing Agreement (DPA) is a legal contract required under GDPR when a service provider processes personal data 
                on behalf of a customer. This DPA clarifies our responsibilities as a data processor and your rights as a data controller.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">1. Definitions and Interpretation</h2>
            <p className="text-gray-600 mb-4">In this DPA:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>"Controller"</strong> means you, the customer, who determines the purposes and means of processing personal data</li>
              <li><strong>"Processor"</strong> means Local Business Heroes, who processes personal data on behalf of the Controller</li>
              <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person</li>
              <li><strong>"Processing"</strong> means any operation performed on personal data (collection, storage, use, disclosure, deletion)</li>
              <li><strong>"Data Subject"</strong> means an identified or identifiable natural person (typically your customers)</li>
              <li><strong>"GDPR"</strong> means the General Data Protection Regulation (EU) 2016/679</li>
              <li><strong>"UK GDPR"</strong> means the GDPR as retained in UK law</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">2. Scope and Application</h2>
            <p className="text-gray-600 mb-4">This DPA applies when:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>You use our Service to process personal data of your customers or contacts</li>
              <li>You store, manage, or send personal data through our platform</li>
              <li>You use our CRM, email, SMS, or other communication features</li>
            </ul>
            <p className="text-gray-600 mb-6">
              This DPA forms part of and is incorporated into our Terms of Service. In the event of any conflict between this DPA 
              and the Terms of Service, this DPA shall prevail with respect to data processing matters.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">3. Your Obligations as Data Controller</h2>
            <p className="text-gray-600 mb-4">As the data controller, you agree to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Comply with all applicable data protection laws when using our Service</li>
              <li>Ensure you have a legal basis for processing personal data (e.g., consent, contract, legitimate interest)</li>
              <li>Obtain necessary consents from data subjects before collecting their personal data</li>
              <li>Provide appropriate privacy notices to your customers</li>
              <li>Only process data that is adequate, relevant, and limited to what is necessary</li>
              <li>Ensure the accuracy of personal data you upload to our Service</li>
              <li>Comply with data retention requirements and delete data when no longer needed</li>
              <li>Inform us immediately if you become aware of any data protection compliance issues</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">4. Our Obligations as Data Processor</h2>
            <p className="text-gray-600 mb-4">We agree to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Process personal data only on your documented instructions (including through use of the Service)</li>
              <li>Ensure that persons authorized to process personal data are bound by confidentiality obligations</li>
              <li>Implement appropriate technical and organizational measures to ensure data security</li>
              <li>Only engage sub-processors with your consent (general authorization is granted through this DPA)</li>
              <li>Assist you in responding to data subject access requests (SARs)</li>
              <li>Assist you in meeting GDPR obligations regarding data security, breach notification, and impact assessments</li>
              <li>Delete or return personal data to you at the end of our agreement, unless required by law to retain it</li>
              <li>Make available information necessary to demonstrate GDPR compliance</li>
              <li>Notify you without undue delay upon becoming aware of a personal data breach</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">5. Data Security Measures</h2>
            <p className="text-gray-600 mb-4">
              We implement industry-standard technical and organizational measures to protect personal data, including:
            </p>
            
            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">Technical Measures:</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li><strong>Encryption:</strong> TLS 1.2+ for data in transit, AES-256 encryption for data at rest</li>
              <li><strong>Access Controls:</strong> Role-based access control (RBAC) with multi-factor authentication</li>
              <li><strong>Network Security:</strong> Firewalls, intrusion detection systems, DDoS protection</li>
              <li><strong>Logging and Monitoring:</strong> Comprehensive audit logs and security monitoring</li>
              <li><strong>Data Backup:</strong> Regular encrypted backups with secure storage</li>
              <li><strong>Vulnerability Management:</strong> Regular security assessments and penetration testing</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">Organizational Measures:</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Staff Training:</strong> Regular data protection and security training for all staff</li>
              <li><strong>Confidentiality:</strong> All staff bound by confidentiality agreements</li>
              <li><strong>Incident Response:</strong> Documented incident response and breach notification procedures</li>
              <li><strong>Vendor Management:</strong> Due diligence on all sub-processors</li>
              <li><strong>Policy Framework:</strong> Comprehensive information security policies</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">6. Sub-processors</h2>
            <p className="text-gray-600 mb-4">
              We may engage third-party sub-processors to assist in providing the Service. You provide general authorization for us to engage 
              sub-processors, provided that:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>We maintain an up-to-date list of sub-processors</li>
              <li>We provide at least 30 days' notice before adding or changing sub-processors</li>
              <li>All sub-processors are bound by data protection obligations equivalent to those in this DPA</li>
              <li>We remain fully liable for the acts and omissions of any sub-processor</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0E163B] mt-6 mb-4">Current Sub-processors:</h3>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <ul className="space-y-3 text-sm text-gray-600">
                <li><strong>Cloud Infrastructure:</strong> Amazon Web Services (AWS) - Data hosting in UK/EU regions</li>
                <li><strong>Payment Processing:</strong> Stripe, Inc. - Secure payment processing</li>
                <li><strong>Email Delivery:</strong> SendGrid/Twilio - Transactional email delivery</li>
                <li><strong>SMS Delivery:</strong> Twilio - SMS message delivery</li>
                <li><strong>Analytics:</strong> Google Analytics - Website analytics (anonymized)</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">7. International Data Transfers</h2>
            <p className="text-gray-600 mb-6">
              Personal data is primarily processed and stored in the United Kingdom and European Economic Area (EEA). 
              If we transfer personal data to countries outside the UK/EEA, we ensure appropriate safeguards are in place:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Standard Contractual Clauses (SCCs) approved by the UK Information Commissioner's Office</li>
              <li>EU Standard Contractual Clauses for EEA transfers</li>
              <li>Adequacy decisions where applicable</li>
              <li>Other appropriate safeguards as required by applicable law</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">8. Data Subject Rights</h2>
            <p className="text-gray-600 mb-4">
              We will assist you in responding to requests from data subjects exercising their rights under GDPR:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li><strong>Right of Access:</strong> We provide tools to export personal data</li>
              <li><strong>Right to Rectification:</strong> You can update inaccurate data through our Service</li>
              <li><strong>Right to Erasure:</strong> You can delete personal data through our Service</li>
              <li><strong>Right to Restriction:</strong> You can limit processing of specific data</li>
              <li><strong>Right to Data Portability:</strong> Export data in machine-readable format (CSV, JSON)</li>
              <li><strong>Right to Object:</strong> Stop processing for direct marketing purposes</li>
            </ul>
            <p className="text-gray-600 mb-6">
              If a data subject contacts us directly, we will redirect them to you as the data controller. 
              You are responsible for responding to data subject requests within the timeframes required by law (typically 30 days).
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">9. Data Breach Notification</h2>
            <p className="text-gray-600 mb-6">
              In the event of a personal data breach affecting your customer data, we will:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Notify you without undue delay after becoming aware of the breach</li>
              <li>Provide information about the nature of the breach, affected data, and likely consequences</li>
              <li>Describe measures taken or proposed to address the breach</li>
              <li>Cooperate with you in investigating and mitigating the breach</li>
              <li>Provide reasonable assistance in notifying data subjects if required</li>
            </ul>
            <p className="text-gray-600 mb-6">
              <strong>Your Responsibility:</strong> As the data controller, you are responsible for determining whether to notify 
              data subjects and/or supervisory authorities about the breach, and for making such notifications within required timeframes.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">10. Data Protection Impact Assessment (DPIA)</h2>
            <p className="text-gray-600 mb-6">
              If required by law, we will provide reasonable cooperation and assistance in conducting Data Protection Impact Assessments 
              for high-risk processing activities. We will provide information about our processing operations, security measures, 
              and risks to help you complete your DPIA.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">11. Audits and Inspections</h2>
            <p className="text-gray-600 mb-6">
              Upon reasonable written notice, you may audit our compliance with this DPA, subject to the following:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Audits must not occur more than once per year unless required by a supervisory authority</li>
              <li>You must provide at least 30 days' advance notice</li>
              <li>Audits must not unreasonably interfere with our business operations</li>
              <li>You must execute a confidentiality agreement before accessing our facilities or systems</li>
              <li>We may require you to use an independent third-party auditor</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Alternatively, you may review our SOC 2 Type II reports and ISO 27001 certificates (when available) to verify our compliance.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">12. Data Retention and Deletion</h2>
            <p className="text-gray-600 mb-6">
              We retain personal data only for as long as necessary to provide the Service or as required by law. Upon termination or expiry 
              of our agreement:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>We will provide you with 30 days to export your data</li>
              <li>After 30 days, we will delete or anonymize all personal data unless legally required to retain it</li>
              <li>Deletion is performed in accordance with industry-standard secure deletion practices</li>
              <li>We will provide written certification of deletion upon request</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">13. Liability and Indemnification</h2>
            <p className="text-gray-600 mb-6">
              Each party's liability under this DPA is subject to the limitations and exclusions of liability set out in the Terms of Service. 
              You agree to indemnify us against claims arising from your breach of this DPA or your obligations as a data controller.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">14. Term and Termination</h2>
            <p className="text-gray-600 mb-6">
              This DPA remains in effect for as long as we process personal data on your behalf. Upon termination, the provisions relating to 
              data deletion, confidentiality, liability, and audit rights shall survive.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">15. Governing Law and Jurisdiction</h2>
            <p className="text-gray-600 mb-6">
              This DPA shall be governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of 
              the courts of England and Wales.
            </p>

            <h2 className="text-2xl font-bold text-[#0E163B] mt-12 mb-6">16. Contact for Data Protection Matters</h2>
            <p className="text-gray-600 mb-6">
              For all data protection and privacy inquiries related to this DPA, please contact:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">dpo@localbusinessheroes.net</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-[#FF5A3D]" />
                  <span className="text-gray-700">UK: +44 20 4634 9070</span>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                This Data Processing Agreement is effective as of {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} 
                and forms an integral part of our Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="p-8 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
            <CardContent className="pt-6">
              <Shield size={64} className="mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Your Data is Protected
              </h2>
              <p className="text-xl opacity-90 mb-6">
                We take data protection seriously. Start using our GDPR-compliant platform with confidence.
              </p>
              <Button 
                asChild
                className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-semibold px-8 py-3"
              >
                <a href="/pricing">
                  Start Your 14-Day Trial
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DPAPage;
